import React, { useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { FaUpload, FaTimes, FaEye } from "react-icons/fa";
import { sanitizeInput } from "./security-utils";
import "./file-upload.css";

const FileUploadComponent = ({
  uploadedFiles = [],
  onFilesAdd,
  onFileRemove,
  maxSize = 5, // MB
  acceptedFormats = ".png,.jpg,.jpeg,.pdf,.doc,.docx,.xls,.xlsx"
}) => {
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const [previewModal, setPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const processFiles = async (files) => {
    const maxSizeBytes = maxSize * 1024 * 1024;
    const validFiles = [];
    const invalidFiles = [];

    // Parse accepted formats if specified
    const acceptedList = acceptedFormats
      ? acceptedFormats.split(",").map((f) => f.trim().toLowerCase())
      : null;

    for (const file of files) {
      const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
      const isFormatAllowed =
        !acceptedList ||
        acceptedList.length === 0 ||
        acceptedList.some(
          (format) =>
            format === ext ||
            (format.startsWith(".") && ext === format) ||
            (file.type && file.type.toLowerCase() === format)
        );

      if (!isFormatAllowed) {
        invalidFiles.push(file.name);
      } else if (file.size > maxSizeBytes) {
        invalidFiles.push(file.name);
      } else {
        try {
          const base64 = await fileToBase64(file);
          validFiles.push({
            file: file,
            name: sanitizeInput(file.name),
            size: file.size,
            type: file.type,
            base64: base64,
          });
        } catch (error) {
          console.error("Error converting file to base64:", error);
          invalidFiles.push(file.name);
        }
      }
    }

    if (validFiles.length > 0 && onFilesAdd) {
      onFilesAdd(validFiles, invalidFiles);
    } else if (invalidFiles.length > 0 && onFilesAdd) {
      onFilesAdd([], invalidFiles);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      await processFiles(files);
    }
    // Reset file input
    e.target.value = "";
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      await processFiles(files);
    }
  };

  const removeFile = (index) => {
    if (onFileRemove) {
      onFileRemove(index);
    }
  };

  const getFileExtension = (fileName) => {
    return fileName.split(".").pop().toUpperCase();
  };

  const isImageFile = (fileName) => {
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp", "svg"];
    const ext = fileName.split(".").pop().toLowerCase();
    return imageExtensions.includes(ext);
  };

  const isPdfFile = (fileName) => {
    const ext = fileName.split(".").pop().toLowerCase();
    return ext === "pdf";
  };

  const isPreviewableFile = (fileName) => {
    return isImageFile(fileName) || isPdfFile(fileName);
  };

  const handlePreview = (fileObj) => {
    if (isImageFile(fileObj.name)) {
      setPreviewData({
        name: fileObj.name,
        data: fileObj.base64,
        type: "image",
        mimeType: fileObj.type,
      });
      setPreviewModal(true);
    } else if (isPdfFile(fileObj.name)) {
      setPreviewData({
        name: fileObj.name,
        data: fileObj.base64,
        type: "pdf",
        mimeType: "application/pdf",
      });
      setPreviewModal(true);
    }
  };

  const closePreviewModal = () => {
    setPreviewModal(false);
    setPreviewData(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  return (
    <div className="file-upload-wrapper">
      {/* Display Uploaded Files */}
      {uploadedFiles && uploadedFiles.length > 0 && (
        <div className="uploaded-files-list mb-3">
          {uploadedFiles.map((fileObj, index) => (
            <div key={index} className="uploaded-file-item">
              <div className="file-icon-wrapper">
                <div className="file-type-badge">
                  {getFileExtension(fileObj.name || fileObj.file?.name || "FILE")}
                </div>
              </div>
              <div className="file-info">
                <div className="file-name">
                  {fileObj.name || fileObj.file?.name || "Unknown"}
                </div>
                <div className="file-size">
                  {formatFileSize(fileObj.size || fileObj.file?.size || 0)}
                </div>
              </div>
              <div className="file-actions">
                {isPreviewableFile(fileObj.name) && (
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => handlePreview(fileObj)}
                    className="file-view-btn"
                    title={isPdfFile(fileObj.name) ? "Preview PDF" : "Preview image"}
                  >
                    <FaEye />
                  </Button>
                )}
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => removeFile(index)}
                  className="file-remove-btn"
                  title="Remove file"
                >
                  <FaTimes />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Area */}
      <div
        className={`file-upload-area ${isDragging ? "drag-active" : ""}`}
        onClick={() => fileInputRef.current?.click()}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="upload-icon">
          <FaUpload size={24} />
        </div>
        <div className="upload-text">
          <div className="upload-primary">
            {isDragging ? (
              <span className="upload-highlight">Drop files here to upload</span>
            ) : (
              <>
                Drag and drop or <span className="upload-highlight">Select Files</span>
              </>
            )}
          </div>
          <div className="upload-secondary">
            You can upload multiple files (max {maxSize}MB each)
          </div>
        </div>
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={acceptedFormats}
        onChange={handleFileUpload}
        style={{ display: "none" }}
      />

      {/* Preview Modal (Image & PDF) */}
      <Modal
        show={previewModal}
        onHide={closePreviewModal}
        centered
        size={previewData?.type === "pdf" ? "xl" : "lg"}
        className="file-preview-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {previewData?.type === "pdf" ? "PDF Preview" : "Image Preview"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          className="d-flex justify-content-center align-items-center"
          style={{
            minHeight: previewData?.type === "pdf" ? "600px" : "400px",
            padding: "20px",
          }}
        >
          {previewData && (
            <div className="preview-container" style={{ width: "100%", height: "100%" }}>
              {previewData.type === "image" && (
                <img
                  src={previewData.data}
                  alt={previewData.name}
                  className="preview-image"
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    borderRadius: "8px",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                  }}
                />
              )}
              {previewData.type === "pdf" && (
                <iframe
                  src={previewData.data}
                  type="application/pdf"
                  width="100%"
                  height="100%"
                  style={{
                    border: "none",
                    borderRadius: "8px",
                    minHeight: "550px",
                  }}
                  title={previewData.name}
                />
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div style={{ width: "100%", textAlign: "center", marginBottom: "8px" }}>
            <small className="text-muted">{previewData?.name}</small>
          </div>
          <Button variant="secondary" onClick={closePreviewModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FileUploadComponent;
